import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { SicotService } from '../sicot.service';
import { Cot } from '../types/Cot';
import { HOME_CARDS, HomeCardConfig } from './home-card-config';

@Component({
    selector: 'app-home',
    imports: [
        CommonModule,
        RouterLink,
        FooterComponent,
        HeaderComponent,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './home.component.html',
    styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly sicot = inject(SicotService);

  /** Centrali COT collegate all'utente loggato. */
  centraliCot: Cot[] = [];
  centraliCotLoading = false;

  /** Gruppi WSO2 dell'utente (display e value) per visibilità card. */
  userGroupNames: Set<string> = new Set();
  groupsLoading = false;

  readonly cards: HomeCardConfig[] = HOME_CARDS;

  /** Card principali (non admin, non nascoste). */
  get mainCards(): HomeCardConfig[] {
    return this.cards.filter(c => !c.admin && !c.hidden);
  }

  /** Card amministrazione (mostrate in griglia separata sotto). */
  get adminCards(): HomeCardConfig[] {
    return this.cards.filter(c => c.admin && !c.hidden);
  }

  /** True se l'utente appartiene a gruppi admin. */
  get isAdmin(): boolean {
    const adminGroups = ['admin', 'Administrator', 'Internal/admin'];
    return adminGroups.some(g => this.userGroupNames.has(g));
  }

  /** True se il pulsante della card deve essere disabilitato. */
  isCardButtonDisabled(card: HomeCardConfig): boolean {
    if (card.disabled) return true;
    if (card.disabledUnlessAdmin && !this.isAdmin) return true;
    return false;
  }

  /** True se c'è almeno una card admin visibile per l'utente. */
  get hasVisibleAdminCards(): boolean {
    return this.adminCards.some(c => this.canShowCard(c));
  }

  ngOnInit(): void {
    this.caricaCentraliCot();
    this.caricaGruppiUtente();
  }

  caricaCentraliCot(): void {
    const userId = this.sicot.getUserId();
    if (!userId) {
      this.centraliCot = [];
      return;
    }
    this.centraliCotLoading = true;
    this.sicot.getUserCots(userId).subscribe({
      next: (list) => {
        this.centraliCot = list ?? [];
        this.centraliCotLoading = false;
      },
      error: () => {
        this.centraliCot = [];
        this.centraliCotLoading = false;
      },
    });
  }

  /** Descrizioni delle centrali COT dell'utente, separate da virgola. */
  get centraliCotDescrizioni(): string {
    return (this.centraliCot ?? []).map(c => c.descrizione || '—').join(', ');
  }

  caricaGruppiUtente(): void {
    const userId = this.sicot.getUserId();
    if (!userId) {
      this.userGroupNames = new Set();
      return;
    }
    this.groupsLoading = true;
    this.sicot.getUsersGroups(userId).subscribe({
      next: (list) => {
        const names = new Set<string>();
        (list ?? []).forEach(g => {
          if (g.display) names.add(g.display);
          if (g.value) names.add(g.value);
        });
        this.userGroupNames = names;
        this.groupsLoading = false;
      },
      error: () => {
        this.userGroupNames = new Set();
        this.groupsLoading = false;
      },
    });
  }

  /** True se la card è visibile per i gruppi dell'utente (nessun allowedGroups = visibile a tutti). */
  canShowCard(card: HomeCardConfig): boolean {
    if (!card.allowedGroups || card.allowedGroups.length === 0) return true;
    return card.allowedGroups.some(g => this.userGroupNames.has(g));
  }

  apriRichiestaValutazione(): void {
    this.router.navigate(['/richiesta-valutazione-assistito/nuova']);
  }
}
